import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { btn } from "@/components/ds/styles";
import { OrderTermsNote } from "@/components/payments/order-terms-note";
import { formatUsd } from "@/lib/payments/currency";
import { queryOptions, useSuspenseQuery, useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { userErrorMessage } from "@/lib/user-error";
import { Check, Clock3, Compass, CreditCard, FileCheck2, ListChecks, Loader2, Sparkles, Upload, UserCheck } from "lucide-react";
import { SMART_SECTION_KEYS, SMART_SECTION_TITLES_AR, SMART_SECTION_TITLES_EN } from "@/lib/bousala/smart-order";
import { DURATION, LANDING } from "@/data/bousala-v1.config";
import ogCover from "@/assets/bousala/og-cover.jpg";
import { supabase } from "@/integrations/supabase/client";
import {
  getBousalaBuyContext,
  submitBousalaLocalPurchase,
  getBousalaReceiptUploadUrl,
  getBousalaResumeState,
  getBousalaMode,
  startBousalaFree,
} from "@/lib/bousala-purchase.functions";
import {
  getBousalaRetakeEligibility,
  retakeBousalaAttempt,
} from "@/lib/bousala/retake.functions";
import { cooldownMessage, retakeCooldownMessage } from "@/lib/bousala/retake-messages";
import { useQueryClient } from "@tanstack/react-query";
import { OUTSIDE_SYRIA_TOGGLE_LABEL_AR } from "@/lib/payments/outside-syria";
import { PaypalReceiptForm } from "@/components/payments/paypal-receipt-form";
import { InsideSyriaPaymentInstructions, PaymentRegionSelector, PaymentStepHeading } from "@/components/payments/payment-flow";
import { RouteError, RouteNotFound } from "@/components/route-boundaries";
import { canonicalLink, canonicalMeta, absoluteUrl } from "@/lib/seo";
import { BousalaLandingEn, SmartBousalaLandingEn } from "@/components/landing/bousala/english-landing";
import { bousalaDescription } from "@/data/bousala-landing";
import { BousalaLandingV3, type SmartBand } from "@/components/landing/bousala/landing-v3";
import { InAppBrowserBanner } from "@/components/common/in-app-browser-banner";
import { useSmartAdminPreview, SmartPreviewBanner } from "@/components/bousala/smart/smart-preview";
import { getSmartPortalState } from "@/lib/bousala/smart-portal.functions";

const ctxQ = () =>
  queryOptions({ queryKey: ["bousala", "buy-context"], queryFn: () => getBousalaBuyContext() });
const smartCtxQ = () =>
  queryOptions({
    queryKey: ["bousala", "buy-context", "smart"],
    queryFn: () => getBousalaBuyContext({ data: { product: "smart" } }),
    staleTime: 60_000,
  });
const modeQ = () =>
  queryOptions({ queryKey: ["bousala", "mode"], queryFn: () => getBousalaMode(), staleTime: 30_000 });

export const Route = createFileRoute("/_public/bousala/")({
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(ctxQ()),
      context.queryClient.ensureQueryData(modeQ()),
      context.queryClient.ensureQueryData(smartCtxQ()),
    ]),
  head: ({ loaderData }) => {
    const smartActive = Boolean(loaderData?.[2]?.product);
    const title = "البوصلة الدراسية من د. سارة النحّاس — التخصص الجامعي الأقرب إلك";
    const description = bousalaDescription(smartActive);
    return ({
    meta: [
      { title },
      { name: "description", content: description },
      { property: "og:title", content: title },
      { property: "og:description", content: description },
      { property: "og:type", content: "website" },
      { property: "og:image", content: absoluteUrl(ogCover) },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "بوصلة د. سارة — Career Compass by Dr. Sara" },
      { name: "twitter:image", content: absoluteUrl(ogCover) },
      { name: "twitter:card", content: "summary_large_image" },
      ...canonicalMeta("/bousala"),
    ],
    links: [canonicalLink("/bousala")],
  });},
  errorComponent: RouteError,
  notFoundComponent: RouteNotFound,
  component: BousalaPage,
});

function BousalaPage() {
  const [context, mode, smartContext] = Route.useLoaderData();
  const queryClient = useQueryClient();
  if (queryClient.getQueryData(ctxQ().queryKey) === undefined) {
    queryClient.setQueryData(ctxQ().queryKey, context);
  }
  if (queryClient.getQueryData(modeQ().queryKey) === undefined) {
    queryClient.setQueryData(modeQ().queryKey, mode);
  }
  if (queryClient.getQueryData(smartCtxQ().queryKey) === undefined) {
    queryClient.setQueryData(smartCtxQ().queryKey, smartContext);
  }
  return <BousalaPageInner />;
}

// Shared client-side language preference for start-test CTAs. Returns {} on
// SSR/first render (stable for hydration) and { lang } after mount.
function useLangSearch(): { lang?: "ar" | "en" } {
  const [lang, setLang] = useState<"ar" | "en" | null>(null);
  useEffect(() => {
    try {
      const v = window.localStorage.getItem("bousala.lang");
      if (v === "ar" || v === "en") setLang(v);
    } catch {}
    const onStorage = (e: StorageEvent) => {
      if (e.key === "bousala.lang" && (e.newValue === "ar" || e.newValue === "en")) setLang(e.newValue);
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);
  return lang ? { lang } : {};
}

function bousalaPlayRedirect(langSearch: { lang?: "ar" | "en" }) {
  return langSearch.lang ? `/bousala/play?lang=${langSearch.lang}` : "/bousala/play";
}

type BousalaLandingLang = "ar" | "en";

const LANDING_COPY: Record<BousalaLandingLang, {
  badge: string;
  h1: string;
  sub: string;
  startPaid: string;
  startFreeSignedIn: string;
  createAndStartFree: string;
  continueTest: string;
  startTest: string;
  viewReport: string;
  freeAccessNote: React.ReactNode;
  paidAccessNote: string;
  fullReportNote: string;
  features: string[];
  sampleTitle: string;
  sampleNote: string;
  sampleAlt: string[];
  trustLine: string;
  parentsBox: string;
  unavailableTitle: string;
  unavailableBody: string;
  productTitle: string;
  productDesc: string;
  freeNow: string;
  freeLimited: string;
  alreadyHaveAccount: string;
  signIn: string;
  startError: string;
  resumeInProgress: string;
  resumeFinished: string;
  restartTest: string;
  restartConfirm: string;
  restartSuccess: string;
}> = {
  ar: {
    badge: "بوصلة د. سارة — الإصدار الأول",
    h1: LANDING.h1,
    sub: LANDING.sub,
    startPaid: "بدء البوصلة الآن",
    startFreeSignedIn: "بدء الاختبار مجاناً",
    createAndStartFree: "إنشاء حساب والبدء مجاناً",
    continueTest: "متابعة الاختبار",
    startTest: "بدء الاختبار",
    viewReport: "عرض التقرير",
    freeAccessNote: <>الوصول للاختبار <b>مجاني بالكامل</b> — كل ما تحتاجه إنشاء حساب.</>,
    paidAccessNote: "الوصول للاختبار مدفوع — يُنشأ حسابك تلقائيًا بعد إتمام الدفع.",
    fullReportNote: "التقرير الكامل يُفتح بعد الشراء — لا نسخة مجانية.",
    features: LANDING.features,
    sampleTitle: "نموذج من التقرير",
    sampleNote: "نموذج حقيقي من تقرير البوصلة — الأسماء والنتائج تجريبية.",
    sampleAlt: ["بصمتك العامة — نموذج", "أعلى ثلاث ذكاءات — نموذج", "التخصصات الأقرب — نموذج", "خطة الـ90 يوماً — نموذج"],
    trustLine: LANDING.trust_line,
    parentsBox: LANDING.parents_box,
    unavailableTitle: "الاختبار غير متاح حاليًا.",
    unavailableBody: "منجرّب كمان مرة بعدين، أو التواصل معنا.",
    productTitle: "بوصلة د. سارة",
    productDesc: "اختبار البوصلة الكامل + التقرير الشخصي.",
    freeNow: "مجانية بالكامل",
    freeLimited: "كل المحاور والتقرير الشخصي — بدون أي تكلفة.",
    alreadyHaveAccount: "عندك حساب مسبق؟",
    signIn: "تسجيل الدخول",
    startError: "تعذّر بدء الاختبار.",
    resumeInProgress: "لديك اختبار قيد التقدم — تقدر تكمل من حيث توقفت.",
    resumeFinished: "اختبارك جاهز — تقريرك الشخصي بانتظارك.",
    restartTest: "البدء من جديد",
    restartConfirm: "للتأكيد، الضغط مرة تانية",
    restartSuccess: "تم تصفير تقدمك — بالتوفيق!",
  },
  en: {
    badge: "Bousala by Dr. Sara — version one",
    h1: "Your compass for choosing your path.",
    sub: "One assessment that reads your intelligences, interests, personality, and values — then points you toward the majors and paths that actually fit you, not the ones that are \u201Csupposed to.\u201D",
    startPaid: "Start Bousala Now",
    startFreeSignedIn: "Start Bousala — free",
    createAndStartFree: "Create account and start free",
    continueTest: "Continue Test",
    startTest: "Start Test",
    viewReport: "View Report",
    freeAccessNote: <>The test is <b>completely free</b> — all you need is an account.</>,
    paidAccessNote: "Access to the test is paid — your account is created automatically after payment.",
    fullReportNote: "The full report opens after purchase — there is no free report version.",
    features: ["8 scientific sections", "Detailed personal report", "90-day plan before the final decision"],
    sampleTitle: "Preview a sample report",
    sampleNote: "A real sample from the Bousala report — names and results are for demonstration.",
    sampleAlt: ["General fingerprint sample", "Top three intelligences sample", "Closest majors sample", "90-day plan sample"],
    trustLine: "Designed by Dr. Sara Nahas using recognized scientific models, including multiple intelligences, career-interest patterns, and personality traits. It is an educational guidance tool, not a psychological diagnosis.",
    parentsBox: "For families: Bousala does not decide on behalf of the student. It helps the student hear their inner voice and gives the family a shared language for support without pressure.",
    unavailableTitle: "The test is not available right now.",
    unavailableBody: "Try again later or contact us.",
    productTitle: "Bousala by Dr. Sara Alnahhas",
    productDesc: "The full Bousala assessment + your personal report.",
    freeNow: "Completely free",
    freeLimited: "All sections and your personal report — at no cost.",
    alreadyHaveAccount: "Already have an account?",
    signIn: "Sign In",
    startError: "We couldn't start the test.",
    resumeInProgress: "You have a test in progress — pick up where you left off.",
    resumeFinished: "Your test is ready — your personal report is waiting.",
    restartTest: "Restart from scratch",
    restartConfirm: "Sure? Tap again to confirm",
    restartSuccess: "Progress cleared — good luck!",
  },
};

type LandingCopy = (typeof LANDING_COPY)[BousalaLandingLang];

function useBousalaLandingLang(): [BousalaLandingLang, (v: BousalaLandingLang) => void] {
  const [lang, setLang] = useState<BousalaLandingLang>("ar");
  useEffect(() => {
    try {
      // A ?lang= URL param (shared links, ads) wins over the stored preference.
      const fromUrl = new URLSearchParams(window.location.search).get("lang");
      if (fromUrl === "ar" || fromUrl === "en") {
        setLang(fromUrl);
        window.localStorage.setItem("bousala.lang", fromUrl);
        return;
      }
      const v = window.localStorage.getItem("bousala.lang");
      if (v === "ar" || v === "en") setLang(v);
    } catch {}
  }, []);

  const pick = (v: "ar" | "en") => {
    setLang(v);
    try {
      window.localStorage.setItem("bousala.lang", v);
      // Same-tab listeners re-read via a synthetic storage event.
      window.dispatchEvent(new StorageEvent("storage", { key: "bousala.lang", newValue: v }));
    } catch {}
  };
  return [lang, pick];
}

function LanguageToggle({ lang, onPick }: { lang: BousalaLandingLang; onPick: (v: BousalaLandingLang) => void }) {
  return (
    <div className="mb-4 flex justify-end" dir="rtl">
      <div className="inline-flex rounded-ds-pill bg-cream-100 p-1 font-display text-[14px] font-bold">
        <button
          type="button"
          onClick={() => onPick("ar")}
          className={lang === "ar" ? "inline-flex min-h-12 items-center rounded-ds-pill bg-surface px-5 text-brown-700 shadow-sm" : "inline-flex min-h-12 items-center rounded-ds-pill px-5 text-brown-500 hover:text-brown-700"}
        >
          العربية
        </button>
        <button
          type="button"
          onClick={() => onPick("en")}
          className={lang === "en" ? "inline-flex min-h-12 items-center rounded-ds-pill bg-surface px-5 text-brown-700 shadow-sm" : "inline-flex min-h-12 items-center rounded-ds-pill px-5 text-brown-500 hover:text-brown-700"}
        >
          English
        </button>
      </div>
    </div>
  );
}

function BousalaPageInner() {
  const { data } = useSuspenseQuery(ctxQ());
  const { data: mode } = useSuspenseQuery(modeQ());
  const [lang, setLang] = useBousalaLandingLang();
  const copy = LANDING_COPY[lang];
  const priceUsd = data.product ? Number(data.product.price_usd) : null;
  const freeMode = Boolean(mode?.free);
  const navigate = useNavigate();
  const email = useSignedInEmail();
  const langSearch = useLangSearch();
  const { data: smartCtx } = useQuery(smartCtxQ());
  const { product: smart, isPreview: smartPreview } = useSmartAdminPreview(
    (smartCtx as any)?.product ?? null,
  );

  const primaryCta = () => {
    if (freeMode) {
      // No account required: the session is created on the first saved answer.
      navigate({ to: "/bousala/play", search: langSearch as any });
      return;
    }
    document.getElementById("checkout")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (lang === "ar") {
    return (
      <ArabicLandingV3
        smart={smart}
        smartPreview={smartPreview}
        freeMode={freeMode}
        onStart={primaryCta}
        checkout={
          !freeMode ? (
            <section id="checkout" className="mx-auto mt-11 max-w-3xl scroll-mt-24 px-5">
              {!data.product ? (
                <div className="rounded-ds-xl border border-hairline-subtle bg-surface p-8 text-center shadow-sm">
                  <p className="text-lg font-bold text-text-primary">{copy.unavailableTitle}</p>
                  <p className="mt-2 text-sm text-text-secondary">{copy.unavailableBody}</p>
                </div>
              ) : (
                <CheckoutBlock
                  priceUsd={priceUsd!}
                  isFree={Boolean((data.product as any)?.is_free)}
                  lang={lang}
                  product={data.product}
                  inside={data.inside_syria}
                  paypal={(data as any).paypal_outside_syria}
                />
              )}
            </section>
          ) : null
        }
        langToggle={<LanguageToggle lang={lang} onPick={setLang} />}
      />
    );
  }

  if (smart) {
    return (
      <SmartFirstLanding
        lang={lang}
        setLang={setLang}
        smart={smart}
        smartPreview={smartPreview}
        freeMode={freeMode}
        copy={copy}
      />
    );
  }

  return (
    <div dir={(lang as BousalaLandingLang) === "ar" ? "rtl" : "ltr"} className="mx-auto max-w-5xl px-5 py-14 md:py-20">
      <InAppBrowserBanner lang={lang} />
      <ResumeBanner />
      <LanguageToggle lang={lang} onPick={setLang} />
      <EnglishHero freeMode={freeMode} copy={copy} onScrollToCheckout={primaryCta} />

      <BousalaChooser lang={lang} copy={copy} freeMode={freeMode} onScrollToCheckout={primaryCta} />

      <BousalaLandingEn onCta={primaryCta} freeMode={freeMode} />

      {!freeMode ? (
        <section id="checkout" className="mt-16 scroll-mt-24">
          <div className="mx-auto max-w-3xl">
            {!data.product ? (
              <div className="rounded-ds-lg bg-card p-8 text-center shadow-soft">
                <p className="text-lg font-bold text-foreground">{copy.unavailableTitle}</p>
                <p className="mt-2 text-sm text-muted-foreground">{copy.unavailableBody}</p>
              </div>
            ) : (
              <CheckoutBlock
                priceUsd={priceUsd!}
                isFree={Boolean((data.product as any)?.is_free)}
                lang={lang}
                product={data.product}
                inside={data.inside_syria}
                paypal={(data as any).paypal_outside_syria}
              />
            )}
          </div>
        </section>
      ) : null}
    </div>
  );
}

/** Redesign v3 Arabic landing: free first; Smart appears as a gated band. */
function ArabicLandingV3({
  smart,
  smartPreview,
  freeMode,
  onStart,
  checkout,
  langToggle,
}: {
  smart: any;
  smartPreview: boolean;
  freeMode: boolean;
  onStart: () => void;
  checkout: React.ReactNode;
  langToggle: React.ReactNode;
}) {
  const order = useSmartOrderKind();
  const hasOrder = Boolean(order.data && order.data.kind !== "none");
  const band: SmartBand | null = smart
    ? {
        price: Number(smart.price_usd),
        href: hasOrder ? "/portal/smart" : "/bousala/smart",
        label: hasOrder ? "متابعة البوصلة الذكية" : "طلب التقرير الذكي",
      }
    : null;
  void freeMode;
  return (
    <div dir="rtl" className="bg-cream-200">
      <div className="mx-auto w-[min(100%-2.5rem,76rem)] pt-4">
        <InAppBrowserBanner lang="ar" />
        <ResumeBanner />
        {langToggle}
        <SmartPreviewBanner show={smartPreview} />
      </div>
      <BousalaLandingV3 onStart={onStart} smart={band} />
      {checkout}
    </div>
  );
}

function useSmartOrderKind() {
  const userId = useSignedInUserId();
  return useQuery({
    queryKey: ["bousala", "smart-portal-state", userId],
    queryFn: () => getSmartPortalState(),
    enabled: Boolean(userId),
    staleTime: 30_000,
    retry: false,
  });
}

function SmartFirstLanding({
  lang,
  setLang,
  smart,
  smartPreview,
  freeMode,
  copy,
}: {
  lang: BousalaLandingLang;
  setLang: (lang: BousalaLandingLang) => void;
  smart: any;
  smartPreview: boolean;
  freeMode: boolean;
  copy: LandingCopy;
}) {
  const price = Number(smart.price_usd);
  const order = useSmartOrderKind();
  const resume = useResumeState();
  const hasOrder = Boolean(order.data && order.data.kind !== "none");
  const hasAttempt = Boolean(
    resume.data?.access === "granted" &&
    resume.data.attemptId &&
    ["in_progress", "completed", "scored", "report_ready"].includes(resume.data.attemptStatus ?? ""),
  );
  const ar = lang === "ar";
  const smartLabel = hasOrder
    ? (ar ? "متابعة البوصلة الذكية" : "Continue Smart Bousala")
    : (ar ? `طلب البوصلة الذكية — ${formatUsd(price)}` : `Get Smart Bousala — ${formatUsd(price)}`);

  return (
    <div dir={ar ? "rtl" : "ltr"} className="mx-auto max-w-5xl px-5 py-14 md:py-20">
      <InAppBrowserBanner lang={lang} />
      <LanguageToggle lang={lang} onPick={setLang} />
      <SmartPreviewBanner show={smartPreview} />
      <SmartHero lang={lang} price={price} label={smartLabel} hasOrder={hasOrder} hasAttempt={hasAttempt} freeMode={freeMode} />
      <SmartPricing
        lang={lang}
        price={price}
        description={null}
        label={smartLabel}
        hasOrder={hasOrder}
        hasAttempt={hasAttempt}
        freeMode={freeMode}
      />
      <SmartDeliveryFlow lang={lang} price={price} />
      <SmartBousalaLandingEn price={price} smartLabel={smartLabel} hasOrder={hasOrder} hasAttempt={hasAttempt} freeMode={freeMode} />
    </div>
  );
}

function SmartPrimaryLink({ label, hasOrder }: { label: string; hasOrder: boolean }) {
  return (
    <Link
      to={hasOrder ? "/portal/smart" : "/bousala/smart"}
      className={`${btn.primary}`}
    >
      {label}
    </Link>
  );
}

function SmartHero({ lang, price, label, hasOrder, hasAttempt, freeMode }: { lang: BousalaLandingLang; price: number; label: string; hasOrder: boolean; hasAttempt: boolean; freeMode: boolean }) {
  const ar = lang === "ar";
  const langSearch = useLangSearch();
  return (
    <section className="pt-3 text-center">
      <div className="inline-flex rounded-full bg-primary/10 px-4 py-2 text-xs font-bold text-primary">
        {ar ? "البوصلة الذكية · من د. سارة النحّاس" : "Smart Bousala · by Dr. Sara Alnahhas"}
      </div>
      <h1 className="mx-auto mt-6 max-w-4xl font-display text-4xl font-extrabold leading-[1.2] text-foreground md:text-6xl">
        {ar ? "اعرف أي تخصص بيشبهك، " : "Find the major that fits you — "}
        <span className="text-modern-orange">{ar ? "بتقرير مكتوب إلك إنت." : "in a report written for you."}</span>
      </h1>
      <p className="mx-auto mt-6 max-w-3xl text-base leading-loose text-muted-foreground md:text-lg">
        {ar
          ? "منبدأ من نتيجتك بالبوصلة، ومنضيف عليها تفضيلاتك: البلد يلي بدك تدرس فيه، وضعك المادي، والتخصصات يلي عم تفكر فيها. النتيجة تقرير مفصّل بـ8 أقسام، بيراجعه فريق د. سارة قبل ما يوصلك."
          : "We start from your Bousala result and add what matters to your decision: where you want to study, your budget, and the majors you're considering. You get a detailed 8-part report, reviewed by Dr. Sara's team before it reaches you."}
      </p>
      <div className="mt-9 flex flex-col items-center justify-center gap-3 sm:flex-row">
        <SmartPrimaryLink label={label} hasOrder={hasOrder} />
        {freeMode && !hasAttempt ? (
          <Link
            to="/bousala/play"
            search={langSearch as any}
            className="inline-flex items-center justify-center px-5 py-3 text-sm font-bold text-foreground underline decoration-border underline-offset-4 transition hover:text-primary md:text-base"
          >
            {ar ? "تجربة البوصلة المجانية" : "Try the free Bousala"}
          </Link>
        ) : null}
      </div>
      <p className="mx-auto mt-5 max-w-3xl text-xs leading-loose text-muted-foreground md:text-sm">
        {ar
          ? "تقريرك خلال 24 ساعة من تعبئة النموذج · مراجعة من فريق د. سارة · الدفع من داخل سوريا أو خارجها"
          : "Within 24 hours of the preferences form · Reviewed by Dr. Sara's team · Pay from inside or outside Syria"}
      </p>
    </section>
  );
}

function SmartPricing({ lang, price, description, label, hasOrder, hasAttempt, freeMode }: { lang: BousalaLandingLang; price: number; description: string | null; label: string; hasOrder: boolean; hasAttempt: boolean; freeMode: boolean }) {
  const ar = lang === "ar";
  return (
    <section id="smart" className="mt-20 scroll-mt-24 md:mt-28">
      <div className="mx-auto max-w-md rounded-ds-lg border-2 border-primary/30 bg-card p-6 shadow-soft md:p-8">
        <div className="flex items-center justify-between gap-3">
          <h2 className="font-display text-2xl font-extrabold text-foreground">{ar ? "البوصلة الذكية" : "Smart Bousala"}</h2>
          <span className="rounded-full bg-primary/15 px-3 py-1 font-display text-sm font-extrabold text-primary">${price.toFixed(0)}</span>
        </div>
        <p className="mt-4 text-sm leading-loose text-muted-foreground">
          {ar ? (description ?? "تقرير مفصّل مكتوب لحالتك، مبني على نتيجتك وتفضيلاتك.") : "A detailed report written around your result, preferences, and real options."}
        </p>
        <ul className="mt-5 grid gap-2">
          {SMART_SECTION_KEYS.map((key) => (
            <li key={key} className="flex items-start gap-2 text-sm text-foreground">
              <Check className="mt-0.5 size-4 shrink-0 text-primary" />
              <span>{ar ? SMART_SECTION_TITLES_AR[key] : SMART_SECTION_TITLES_EN[key]}</span>
            </li>
          ))}
        </ul>
        <div className="mt-6 space-y-2 border-t border-border pt-5 text-xs text-muted-foreground">
          <p>✓ {ar ? "خلال 24 ساعة من تعبئة النموذج" : "Within 24 hours of the preferences form"}</p>
          <p>✓ {ar ? "مراجعة بشرية من فريق د. سارة" : "Human review by Dr. Sara's team"}</p>
          <p>✓ {ar ? "مبني على نتيجتك وتفضيلاتك" : "Built on your result and preferences"}</p>
        </div>
        <div className="mt-6"><SmartPrimaryLink label={label} hasOrder={hasOrder} /></div>
      </div>
      {freeMode && !hasAttempt ? (
        <div className="mt-5 text-center">
          <Link to="/bousala/play" search={{ lang } as any} className="inline-flex min-h-11 items-center text-sm font-bold text-primary underline underline-offset-4">
            {ar ? "أو تجربة البوصلة المجانية" : "Or try the free Bousala"}
          </Link>
        </div>
      ) : null}
      <ResumeBanner compact />
    </section>
  );
}

const FLOW_ICONS = [CreditCard, Compass, ListChecks, FileCheck2] as const;
function SmartDeliveryFlow({ lang, price }: { lang: BousalaLandingLang; price: number }) {
  const ar = lang === "ar";
  const steps = ar ? [
    ["ادفع", `${formatUsd(price)} عبر PayPal من برّا سوريا، أو شام كاش / الهرم للحوالات من جوّا.`],
    ["اعمل البوصلة", `اختبار على 8 محاور (${DURATION.label_ar})، وفيك تبلّش فوراً بعد الدفع. إذا عملتها قبل، منعتمد نتيجتك.`],
    ["عبّي نموذج التفضيلات", "البلد، وضعك المادي، التخصصات يلي عم تفكر فيها، وتوقعات أهلك."],
    ["تقريرك خلال 24 ساعة", "من لحظة ما تبعت النموذج، بيراجعه فريق د. سارة وبيوصلك على حسابك وواتساب."],
  ] : [
    ["Pay", `${formatUsd(price)} via PayPal from outside Syria, or Sham Cash / Al-Haram from inside.`],
    ["Take the Bousala", "An 8-dimension test you can start right after paying. Already took it? We use your result."],
    ["Fill in the preferences form", "Country, budget, the majors you're weighing, and your family's expectations."],
    ["Your report within 24 hours", "From submitting the form, reviewed by Dr. Sara's team, delivered to your account and WhatsApp."],
  ];
  return (
    <section className="mt-20 md:mt-28">
      <h2 className="text-center font-display text-3xl font-extrabold text-foreground md:text-4xl">{ar ? "كيف بتوصل لتقريرك؟" : "How you get your report"}</h2>
      <ol className="relative mx-auto mt-10 max-w-5xl space-y-0 md:grid md:grid-cols-4 md:space-y-0">
        {steps.map(([title, body], index) => (
          <li key={title} className="relative grid grid-cols-[2.75rem_1fr] gap-4 pb-8 last:pb-0 md:block md:px-3 md:pb-0 md:text-center">
            {index < steps.length - 1 ? <span className="absolute bottom-0 start-[1.35rem] top-11 w-px bg-border md:bottom-auto md:start-1/2 md:top-[1.35rem] md:h-px md:w-full" /> : null}
            <span className="relative z-10 flex size-11 items-center justify-center rounded-full bg-primary font-display text-sm font-extrabold text-primary-foreground">{index + 1}</span>
            <div className="md:mt-5">
              <h3 className="flex items-center gap-2 font-display text-base font-extrabold text-foreground md:justify-center">
                {(() => { const Icon = FLOW_ICONS[index]; return Icon ? <Icon className="size-4 text-primary" aria-hidden /> : null; })()}
                {title}
              </h3>
              <p className="mt-2 text-sm leading-loose text-muted-foreground">{body}</p>
            </div>
          </li>
        ))}
      </ol>
    </section>
  );
}

function useSignedInEmail() {
  const [email, setEmail] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setEmail(data.session?.user.email ?? null));
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setEmail(s?.user.email ?? null));
    return () => data.subscription.unsubscribe();
  }, []);
  return email;
}

/** Anonymous sessions have no email — key session-scoped queries off the id. */
function useSignedInUserId() {
  const [userId, setUserId] = useState<string | null>(null);
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setUserId(data.session?.user.id ?? null));
    const { data } = supabase.auth.onAuthStateChange((_e, s) => setUserId(s?.user.id ?? null));
    return () => data.subscription.unsubscribe();
  }, []);
  return userId;
}

function useResumeState() {
  const userId = useSignedInUserId();
  return useQuery({
    queryKey: ["bousala", "resume-state", userId],
    enabled: Boolean(userId),
    queryFn: () => getBousalaResumeState(),
    staleTime: 30_000,
  });
}

function ResumeBanner({ compact = false }: { compact?: boolean }) {
  const { data } = useResumeState();
  const langSearch = useLangSearch();
  const [lang] = useBousalaLandingLang();
  const copy = LANDING_COPY[lang];
  const navigate = useNavigate();
  const qc = useQueryClient();
  const restart = useServerFn(retakeBousalaAttempt);
  const fnEligibility = useServerFn(getBousalaRetakeEligibility);
  const [confirming, setConfirming] = useState(false);
  const [restarting, setRestarting] = useState(false);
  const eligibility = useQuery({
    queryKey: ["bousala", "retake-eligibility", data?.attemptId ?? null],
    queryFn: () => fnEligibility(),
    enabled: Boolean(data?.attemptId) && data?.access === "granted",
    staleTime: 60_000,
  });
  if (!data) return null;
  if (data.access !== "granted") return null;
  const attemptId = data.attemptId ?? null;
  const elig = eligibility.data;
  const hasOpenAttempt = Boolean(elig?.openAttemptId);
  const lockedUntil = !hasOpenAttempt && elig && !elig.canRetake ? elig.unlockAt : null;
  const onRestart = async () => {
    if (!attemptId) return;
    if (hasOpenAttempt) {
      navigate({ to: "/bousala/play", search: langSearch as any });
      return;
    }
    if (!confirming) {
      setConfirming(true);
      window.setTimeout(() => setConfirming(false), 4000);
      return;
    }
    setRestarting(true);
    try {
      await restart({ data: { attemptId, lang } });
      await qc.invalidateQueries({ queryKey: ["bousala", "resume-state"] });
      toast.success(copy.restartSuccess);
      navigate({ to: "/bousala/play", search: langSearch as any });
    } catch (e: any) {
      const cooldown = retakeCooldownMessage(e, lang);
      if (cooldown) {
        await qc.invalidateQueries({ queryKey: ["bousala", "retake-eligibility"] });
        toast.error(cooldown);
        return;
      }
      toast.error(userErrorMessage(e, lang, copy.startError));
    } finally {
      setRestarting(false);
      setConfirming(false);
    }
  };
  const restartLabel = restarting
    ? "…"
    : hasOpenAttempt
      ? copy.continueTest
      : confirming
        ? copy.restartConfirm
        : copy.restartTest;
  const RestartButton = attemptId ? (
    <button
      type="button"
      onClick={onRestart}
      disabled={restarting || eligibility.isLoading || Boolean(lockedUntil)}
      className="mt-3 inline-flex min-h-11 items-center rounded-full border border-primary/40 bg-transparent px-6 py-2.5 text-sm font-bold text-primary transition hover:bg-primary/10 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {restartLabel}
    </button>
  ) : null;
  const LockedNote = lockedUntil ? (
    <p className="mt-2 w-full text-xs font-bold text-muted-foreground">
      {cooldownMessage(lockedUntil, lang)}
    </p>
  ) : null;
  if (data.attemptStatus === "in_progress" && data.attemptId) {
    return (
      <div className={compact ? "mx-auto mt-4 max-w-2xl border-y border-primary/20 py-4 text-center" : "mb-8 rounded-ds-lg border border-primary/30 bg-primary/10 p-4 text-center md:p-5"} dir={lang === "ar" ? "rtl" : "ltr"}>
        <p className="font-display text-sm font-bold text-foreground md:text-base">
          {copy.resumeInProgress}
        </p>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/bousala/play"
            search={langSearch as any}
            className={compact ? "mt-3 inline-flex items-center text-sm font-bold text-primary underline underline-offset-4" : `${btn.primary} mt-3`}
          >
            {copy.continueTest}
          </Link>
          {RestartButton}
        </div>
        {LockedNote}
      </div>
    );
  }
  if (
    (data.attemptStatus === "completed" ||
      data.attemptStatus === "scored" ||
      data.attemptStatus === "report_ready") &&
    data.attemptId
  ) {
    return (
      <div className={compact ? "mx-auto mt-4 max-w-2xl border-y border-soft-green/30 py-4 text-center" : "mb-8 rounded-ds-lg border border-soft-green/40 bg-soft-green/20 p-4 text-center md:p-5"} dir={lang === "ar" ? "rtl" : "ltr"}>
        <p className="font-display text-sm font-bold text-foreground md:text-base">
          {copy.resumeFinished}
        </p>
        <div className="mt-1 flex flex-wrap items-center justify-center gap-3">
          <Link
            to="/portal/bousala/$attemptId"
            params={{ attemptId: data.attemptId }}
            className={compact ? "mt-3 inline-flex items-center text-sm font-bold text-primary underline underline-offset-4" : `${btn.primary} mt-3`}
          >
            {copy.viewReport}
          </Link>
          {RestartButton}
        </div>
        {LockedNote}
      </div>
    );
  }
  return null;
}


/**
 * The one place where the two Bousalas are compared. The Smart card renders
 * only when the `bousala-smart` product row is active — before launch this
 * whole section is absent, no "coming soon" placeholder.
 */
function BousalaChooser({
  lang,
  copy,
  freeMode,
  onScrollToCheckout,
}: {
  lang: BousalaLandingLang;
  copy: LandingCopy;
  freeMode?: boolean;
  onScrollToCheckout: () => void;
}) {
  const { data: smartCtx } = useQuery(smartCtxQ());
  // Public gate, plus the admin live preview of the unlaunched product.
  const { product: smart, isPreview: smartPreview } = useSmartAdminPreview(
    (smartCtx as any)?.product ?? null,
  );
  if (!smart) return null;
  const price = Number(smart.price_usd);
  const ar = lang === "ar";

  const freeBullets = ar
    ? ["8 محاور", DURATION.label_ar, "تقريرك الشخصي فور الانتهاء"]
    : ["8 dimensions", DURATION.label_en, "Your personal report as soon as you finish"];
  const assurances = ar
    ? [
        { icon: Clock3, text: "خلال 24 ساعة من تعبئة نموذج التفضيلات" },
        { icon: UserCheck, text: "مراجعة بشرية من فريق د. سارة" },
        { icon: Sparkles, text: "مبني على نتيجتك" },
      ]
    : [
        { icon: Clock3, text: "Within 24 hours of the preferences form" },
        { icon: UserCheck, text: "Reviewed by Dr. Sara's team" },
        { icon: Sparkles, text: "Built on your own result" },
      ];

  return (
    <section id="smart" className="mt-16 scroll-mt-24 md:mt-20" dir={ar ? "rtl" : "ltr"}>
      <SmartPreviewBanner show={smartPreview} />
      <div className="text-center">
        <h2 className="font-display text-3xl font-extrabold text-foreground md:text-4xl">
          {ar ? "اختر البوصلة المناسبة لك" : "Choose your Bousala"}
        </h2>
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2">
        <div className="flex flex-col rounded-ds-lg bg-card p-7 shadow-soft">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-xl font-extrabold text-foreground">
              {ar ? "البوصلة" : "Bousala"}
            </h3>
            <span className="rounded-full bg-soft-green/25 px-3 py-1 text-xs font-bold text-foreground">
              {ar ? "مجانية" : "Free"}
            </span>
          </div>
          <ul className="mt-5 flex-1 space-y-2.5">
            {freeBullets.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-foreground">
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-6">
            <SmartHeroCta onScrollToCheckout={onScrollToCheckout} freeMode={freeMode} copy={copy} />
          </div>
        </div>

        <div className="flex flex-col rounded-ds-lg border-2 border-primary/30 bg-card p-7 shadow-soft">
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-xl font-extrabold text-foreground">
              {ar ? "البوصلة الذكية" : "Smart Bousala"}
            </h3>
            <span className="rounded-full bg-primary/15 px-3 py-1 font-display text-sm font-extrabold text-primary">
              {formatUsd(price)}
            </span>
          </div>
          <p className="mt-4 text-sm leading-loose text-muted-foreground">
            {ar
              ? "تقرير مفصّل مكتوب لحالتك أنت، مبني على نتيجة البوصلة وتفضيلاتك."
              : "A detailed report written for your own case, built on your Bousala result and your preferences."}
          </p>
          <ul className="mt-5 grid flex-1 gap-2 sm:grid-cols-2">
            {SMART_SECTION_KEYS.map((key) => (
              <li key={key} className="flex items-start gap-2 text-xs text-foreground">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                <span>{SMART_SECTION_TITLES_AR[key]}</span>
              </li>
            ))}
          </ul>
          <ul className="mt-5 space-y-2">
            {assurances.map((a) => {
              const Icon = a.icon;
              return (
                <li key={a.text} className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" />
                  <span>{a.text}</span>
                </li>
              );
            })}
          </ul>
          <div className="mt-6">
            <Link
              to="/bousala/smart"
              className={`${btn.primary}`}
            >
              {ar ? "اطلب البوصلة الذكية" : "Order Smart Bousala"}
            </Link>
            <p className="mt-3 text-xs text-muted-foreground">
              {ar
                ? "ادفع ← اعمل البوصلة ← عبّي نموذج التفضيلات ← تقريرك خلال 24 ساعة"
                : "Pay → take the Bousala → fill in the preferences form → report within 24 hours"}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function SmartHeroCta({
  onScrollToCheckout,
  freeMode,
  copy,
  quiet = false,
}: {
  onScrollToCheckout: () => void;
  freeMode?: boolean;
  copy: LandingCopy;
  quiet?: boolean;
}) {
  const { data } = useResumeState();
  const email = useSignedInEmail();
  const langSearch = useLangSearch();
  const isSignedIn = Boolean(email);
  if (data?.access === "granted" && data.attemptStatus === "in_progress" && data.attemptId) {
    return (
      <Link
        to="/bousala/play"
        search={langSearch as any}
        className={quiet ? `${btn.link} underline` : `${btn.primary}`}
      >
        {copy.continueTest}
      </Link>
    );
  }
  if (
    data?.access === "granted" &&
    (data.attemptStatus === "completed" ||
      data.attemptStatus === "scored" ||
      data.attemptStatus === "report_ready") &&
    data.attemptId
  ) {
    return (
      <Link
        to="/portal/bousala/$attemptId"
        params={{ attemptId: data.attemptId }}
        className={quiet ? `${btn.link} underline` : `${btn.primary}`}
      >
        {copy.viewReport}
      </Link>
    );
  }
  if (data?.access === "granted") {
    // Access but no attempt yet
    return (
      <Link
        to="/bousala/play"
        search={langSearch as any}
        className={quiet ? `${btn.link} underline` : `${btn.primary}`}
      >
        {copy.startTest}
      </Link>
    );
  }
  if (freeMode) {
    // No account needed — the session is created on the first saved answer.
    return (
      <Link
        to="/bousala/play"
        search={langSearch as any}
        className={quiet ? `${btn.link} underline` : `${btn.primary}`}
      >
        {isSignedIn ? copy.startFreeSignedIn : copy.startTest}
      </Link>
    );
  }
  return (
    <button
      type="button"
      onClick={onScrollToCheckout}
      className={quiet ? `${btn.link} underline` : `${btn.primary}`}
    >
      {copy.startPaid}
    </button>
  );
}

function EnglishHero({
  freeMode,
  copy,
  onScrollToCheckout,
}: {
  freeMode?: boolean;
  copy: LandingCopy;
  onScrollToCheckout: () => void;
}) {
  return (
    <section className="relative pt-2 text-center" dir="ltr">
      {freeMode ? (
        <div className="inline-flex items-center gap-2 rounded-full border border-soft-green/40 bg-surface-green/70 px-4 py-1.5 text-xs font-bold text-soft-green">
          <span className="relative flex h-2 w-2">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-soft-green opacity-70" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-soft-green" />
          </span>
          Completely free
        </div>
      ) : (
        <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-4 py-1.5 text-xs font-bold text-primary">
          <Compass className="h-3.5 w-3.5" />
          {copy.badge}
        </div>
      )}

      <p className="mt-6 kicker text-modern-orange">A tool by Dr. Sara Alnahhas</p>

      <h1 className="mx-auto mt-4 max-w-3xl font-display text-4xl font-extrabold leading-[1.15] text-foreground md:text-6xl">
        Your compass for{" "}
        <span className="text-modern-orange">choosing your path.</span>
      </h1>

      <p className="mx-auto mt-6 max-w-2xl text-base leading-loose text-muted-foreground md:text-lg">
        {copy.sub}
      </p>

      <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
        <SmartHeroCta
          onScrollToCheckout={onScrollToCheckout}
          freeMode={freeMode}
          copy={copy}
        />
        <a
          href="#how"
          onClick={(e) => {
            e.preventDefault();
            document
              .getElementById("how")
              ?.scrollIntoView({ behavior: "smooth", block: "start" });
          }}
          className={btn.secondary}
        >
          See how it works
        </a>
      </div>

      <p className="mt-5 text-xs text-muted-foreground md:text-sm">
        {DURATION.label_en} · 8 dimensions · Full report on completion
      </p>
    </section>
  );
}

function FreeModeBlock({
  product,
  copy,
}: {
  product: { title_ar: string; description_ar: string | null };
  copy: LandingCopy;
}) {
  const email = useSignedInEmail();
  const navigate = useNavigate();
  const grant = useServerFn(startBousalaFree);
  const langSearch = useLangSearch();
  const [loading, setLoading] = useState(false);

  async function startFree() {
    if (loading) return;
    setLoading(true);
    try {
      await grant({ data: { lang: langSearch.lang } });
      navigate({ to: "/bousala/play", search: langSearch as any });
    } catch (err: any) {
      toast.error(userErrorMessage(err, langSearch.lang === "en" ? "en" : "ar", copy.startError));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="rounded-ds-lg bg-card p-6 text-center shadow-soft md:p-8">
      <p className="text-xs font-bold tracking-widest text-primary">BOUSALA</p>
      <h2 className="mt-2 font-display text-2xl font-extrabold text-foreground md:text-3xl">{copy.productTitle || product.title_ar}</h2>
      <p className="mt-3 text-sm text-muted-foreground">
        {copy.productDesc || product.description_ar}
      </p>
      <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-soft-green/20 px-5 py-2 font-display text-lg font-extrabold text-soft-green">
        {copy.freeNow}
      </div>
      <p className="mt-3 text-xs text-muted-foreground">{copy.freeLimited}</p>

      <div className="mt-6">
        {email ? (
          <button
            type="button"
            onClick={startFree}
            disabled={loading}
            className={`${btn.primary} disabled:opacity-50`}
          >
            {loading && <Loader2 className="h-4 w-4 animate-spin" />}
            {copy.startFreeSignedIn}
          </button>
        ) : (
          <Link
            to="/bousala/play"
            search={langSearch as any}
            className={`${btn.primary}`}
          >
            {copy.startTest}
          </Link>
        )}
      </div>

      <p className="mt-6 text-xs text-muted-foreground">
        {copy.alreadyHaveAccount}{" "}
        <Link
          to="/auth"
          search={{ mode: "sign_in", redirect: bousalaPlayRedirect(langSearch) } as any}
          className="font-bold text-primary hover:underline"
        >
          {copy.signIn}
        </Link>
      </p>
    </div>
  );
}

function CheckoutBlock({
  priceUsd,
  isFree,
  lang,
  product,
  inside,
  paypal,
}: {
  priceUsd: number;
  isFree: boolean;
  lang: "ar" | "en";
  product: { title_ar: string; description_ar: string | null };
  inside: any;
  paypal: any;
}) {
  const [country, setCountry] = useState<"outside" | "SY">("outside");
  return (
    <>
      <header className="mb-8 text-center">
        <p className="text-xs font-bold tracking-widest text-primary">BOUSALA</p>
        <h2 className="mt-2 font-display text-2xl font-extrabold text-foreground md:text-3xl">
          {product.title_ar}
        </h2>
        <p className="mt-3 text-sm text-muted-foreground">
          {product.description_ar ?? "اختبار البوصلة الكامل + التقرير الشخصي."}
        </p>
        <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-primary/10 px-5 py-2 font-display text-lg font-extrabold text-primary">
          {isFree ? (lang === "en" ? "Free" : "مجاني") : formatUsd(priceUsd)}
        </div>
      </header>

      <PaymentRegionSelector
        value={country}
        onChange={setCountry}
        outsideLabel={OUTSIDE_SYRIA_TOGGLE_LABEL_AR}
        insideLabel="من داخل سوريا (تحويل داخلي)"
      />

      {country === "outside" ? (
        <BousalaPaypalForm priceUsd={priceUsd} instructions={paypal} />
      ) : (
        <LocalBuyForm insideInstructions={inside} />
      )}


      <p className="mt-8 text-center text-xs text-muted-foreground">
        عندك حساب مسبق؟{" "}
        <Link to="/auth" search={{ mode: "sign_in" } as any} className="font-bold text-primary hover:underline">
          سجّل الدخول
        </Link>
      </p>
    </>
  );
}

function BousalaPaypalForm({ priceUsd, instructions }: { priceUsd: number; instructions: any }) {
  const getUploadUrl = useServerFn(getBousalaReceiptUploadUrl);
  const submit = useServerFn(submitBousalaLocalPurchase);
  const navigate = useNavigate();
  return (
    <PaypalReceiptForm
      idPrefix="bousala-paypal"
      priceUsd={priceUsd}
      instructions={instructions}
      getUploadUrl={(filename) => getUploadUrl({ data: { filename } })}
      submit={(payload) => submit({ data: { ...payload, product: "v1" } })}
      onDone={() => navigate({ to: "/bousala/pending", search: { product: "v1", rail: "paypal" } as any })}
    />
  );
}

function LocalBuyForm({ insideInstructions }: { insideInstructions: any }) {
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const getUploadUrl = useServerFn(getBousalaReceiptUploadUrl);
  const submit = useServerFn(submitBousalaLocalPurchase);
  const navigate = useNavigate();
  const langSearch = useLangSearch();
  const en = langSearch.lang === "en";
  const t = en
    ? {
        allRequired: "All fields are required.",
        uploadReceipt: "Please upload your payment receipt.",
        tooLarge: "Receipt must be under 5MB.",
        wrongType: "Please upload an image or a PDF file.",
        received: "We received your payment proof — we'll contact you on WhatsApp.",
        failed: "Couldn't submit. Please try again.",
      }
    : {
        allRequired: "جميع الحقول مطلوبة.",
        uploadReceipt: "يرجى رفع إيصال الدفع.",
        tooLarge: "الحجم الأقصى للإيصال 5 ميغابايت.",
        wrongType: "يرجى رفع صورة أو ملف PDF.",
        received: "استلمنا إثبات الدفع — سنتواصل معك على واتساب.",
        failed: "تعذّر إتمام الإرسال. منجرّب كمان مرة؟",
      };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (loading) return;
    if (!fullName || !email || !phone) {
      toast.error(t.allRequired);
      return;
    }
    if (!file) {
      toast.error(t.uploadReceipt);
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(t.tooLarge);
      return;
    }
    if (!/^(image\/|application\/pdf)/.test(file.type)) {
      toast.error(t.wrongType);
      return;
    }
    setLoading(true);
    try {
      const { path, token } = await getUploadUrl({ data: { filename: file.name } });
      const { error: upErr } = await supabase.storage
        .from("payment-receipts")
        .uploadToSignedUrl(path, token, file, { contentType: file.type });
      if (upErr) throw upErr;

      const result = await submit({
        data: {
          email: email.trim(),
          full_name: fullName.trim(),
          phone: phone.trim(),
          receipt_path: path,
          lang: langSearch.lang,
        },
      });
      toast.success(t.received);
      navigate({
        to: "/bousala/pending",
        search: { granted: result.alreadyGranted ? 1 : 0 } as any,
      });
    } catch (err: any) {
      toast.error(userErrorMessage(err, "ar", t.failed));
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={onSubmit} className="space-y-8">
      {insideInstructions?.body_md ? (
        <section>
          <PaymentStepHeading number={1}>حوّل المبلغ</PaymentStepHeading>
          <InsideSyriaPaymentInstructions markdown={insideInstructions.body_md} title={insideInstructions.title_ar} />
        </section>
      ) : null}

      <section className="rounded-ds-md bg-card p-4 shadow-soft sm:p-5">
        <PaymentStepHeading number={2}>ارفع الإيصال</PaymentStepHeading>
        <div className="grid gap-4">
        <Field label="الاسم الكامل" value={fullName} onChange={setFullName} required autoComplete="name" />
        <Field label="البريد الإلكتروني" value={email} onChange={setEmail} type="email" required autoComplete="email" />
        <Field label="رقم واتساب (مع رمز الدولة)" value={phone} onChange={setPhone} type="tel" required autoComplete="tel" placeholder="+9639xxxxxxxx" />

        <label className="block text-sm">
          <span className="mb-1 block font-semibold text-foreground">إيصال الدفع (صورة أو PDF، حتى 5MB)</span>
          <div className="grid min-w-0 grid-cols-[auto_minmax(0,1fr)] items-center gap-3 rounded-ds-sm border-2 border-dashed border-input p-3">
            <Upload className="h-5 w-5 text-muted-foreground" />
            <input
              type="file"
              accept="image/*,application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="block min-w-0 w-full text-sm text-foreground file:mr-3 file:rounded-full file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-bold file:text-primary-foreground"
            />
          </div>
        </label>
        </div>

      <OrderTermsNote />
      <button
        type="submit"
        disabled={loading}
        className={`${btn.primary} mt-5 w-full disabled:opacity-50`}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" />}
        إرسال إثبات الدفع
      </button>
      <p className="mt-3 text-center text-xs text-muted-foreground">
        بعد المراجعة سنرسل لك رابط تفعيل الحساب عبر واتساب.
      </p>
      </section>
    </form>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  required,
  placeholder,
  autoComplete,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  placeholder?: string;
  autoComplete?: string;
}) {
  return (
    <label className="block text-sm">
      <span className="mb-1 block font-semibold text-foreground">{label}</span>
      <input
        type={type}
        value={value}
        required={required}
        placeholder={placeholder}
        autoComplete={autoComplete}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-ds-sm border border-input bg-background px-3 py-2.5 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30"
      />
    </label>
  );
}